import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';
import { getApiOrigin } from '../apiBase';

function typeIcon(type) {
    const icons = {
        attendance: '📋',
        payment: '💳',
        billing: '💰',
        invite: '📨',
        schedule: '📅',
        system: '⚙️',
    };
    return icons[type] || '🔔';
}

export default function NotificationBell() {
    const { t } = useTranslation('notifications');
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadItems = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        setLoading(true);
        try {
            const res = await fetch(`${getApiOrigin()}/api/notifications?page=1&pageSize=30`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setItems(data.items || []);
            }
        } catch {
            setItems([]);
        }
        setLoading(false);
    }, []);

    const { unreadCount, markAllRead, refreshUnread } = useNotifications({
        onNotification: (n) => {
            setItems((prev) => [{ ...n, isRead: false }, ...prev]);
        },
    });

    useEffect(() => {
        if (open) loadItems();
    }, [open, loadItems]);

    const handleMarkAll = async () => {
        await markAllRead();
        setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
        refreshUnread();
    };

    return (
        <div className="sp-notification-bell" style={{ position: 'relative' }}>
            <button
                type="button"
                className="sp-header__profile"
                onClick={() => setOpen((v) => !v)}
                title={t('title')}
                aria-label={t('title')}
                style={{ position: 'relative', border: 'none', cursor: 'pointer' }}
            >
                🔔
                {unreadCount > 0 && (
                    <span
                        style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            background: '#EB3333',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 10,
                            minWidth: 18,
                            height: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 4px',
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <>
                    <div
                        role="presentation"
                        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                        onClick={() => setOpen(false)}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: 'calc(100% + 8px)',
                            width: 320,
                            maxHeight: 400,
                            overflow: 'auto',
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                            zIndex: 9999,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                borderBottom: '1px solid #eee',
                                gap: 8,
                            }}
                        >
                            <strong style={{ flex: 1 }}>{t('title')}</strong>
                            {unreadCount > 0 && (
                                <button
                                    type="button"
                                    onClick={handleMarkAll}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#007AFF',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                    }}
                                >
                                    {t('markAllRead')}
                                </button>
                            )}
                        </div>
                        {loading ? (
                            <p style={{ padding: 16, color: '#888' }}>...</p>
                        ) : items.length === 0 ? (
                            <p style={{ padding: 24, color: '#888', textAlign: 'center' }}>
                                {t('empty')}
                            </p>
                        ) : (
                            items.map((n) => (
                                <div
                                    key={n.id ?? `${n.title}-${n.createdAt}`}
                                    style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f0f0f0',
                                        background: n.isRead ? '#fff' : '#FFF8F8',
                                        display: 'flex',
                                        gap: 10,
                                    }}
                                >
                                    <span>{typeIcon(n.type)}</span>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{n.title}</div>
                                        <div style={{ fontSize: 13, color: '#666' }}>{n.body}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
